import * as React from "react";

import { ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import { IcTboContainer } from "@racoon/icons";
import { Popover, PopoverContent, PopoverTrigger, cn } from "@racoon/ui";

import { ConsignmentStepper } from "~/shared/ui/consignment-inputs";
import { COEFFICIENT_OPTIONS, plannedVolume } from "../model/container-volume";
import { useContainerNote } from "../model/use-container-note";

// Figma TBO_container variants; the backend also accepts values in between.
const coefficientColor = (value: number) => {
  if (value === 0) return "text-red-500";
  if (value <= 0.5) return "text-blue-600";
  if (value <= 0.75) return "text-violet-500";
  if (value <= 1) return "text-green-500";
  return "text-orange-500";
};

const coefficientLabel = (value: number) =>
  Number.isInteger(value) && value !== 0 ? value.toFixed(1) : String(value);

const TboContainer: React.FC<{ value: number; className?: string }> = ({ value, className }) => (
  <span
    className={cn(
      "relative flex size-8 shrink-0 items-center justify-center",
      coefficientColor(value),
      className,
    )}
  >
    <IcTboContainer width={value === 0 ? 28 : 30} height={28} />
    <span className="absolute left-1/2 top-[calc(50%-5px)] -translate-x-1/2 whitespace-nowrap text-[10px] font-bold leading-[9px] tracking-[-0.05px] text-white">
      {coefficientLabel(value)}
    </span>
  </span>
);

const CoefficientPicker: React.FC<{
  value: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}> = ({ value, disabled, onChange }) => {
  const t = useT();
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        disabled={disabled}
        aria-label={t(Namespace.PhotoReport, "containerFill")}
        className="rounded disabled:cursor-not-allowed"
      >
        <TboContainer value={value} />
      </PopoverTrigger>
      <PopoverContent width="custom" className="flex w-auto gap-2 p-2">
        {COEFFICIENT_OPTIONS.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => {
              setOpen(false);
              if (option !== value) onChange(option);
            }}
            className={cn(
              "rounded-lg p-1 hover:bg-slate-100",
              option === value && "ring-2 ring-blue-600",
            )}
          >
            <TboContainer value={option} />
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

export const ContainerCoefficientsBar: React.FC<{
  job: ConnectProto.Job;
  onChange: (job: ConnectProto.Job) => void;
  disabled?: boolean;
}> = ({ job, onChange, disabled }) => {
  const t = useT();
  const note = useContainerNote(job, onChange);
  const planned = plannedVolume(job);
  const countLabel = t(Namespace.PhotoReport, "containerCount");

  return (
    <div className="flex w-full items-center justify-between gap-4 rounded-lg bg-slate-100 p-4">
      <div className="flex flex-wrap items-center gap-4">
        {note.coefficients.map((coefficient, index) => (
          <CoefficientPicker
            key={index}
            value={coefficient}
            disabled={disabled}
            onChange={(value) => note.setCoefficient(index, value)}
          />
        ))}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <div
          title={countLabel}
          className="flex h-8 items-center rounded-2xl border border-border-primary bg-white px-1"
        >
          {/* Only the buttons change the count, one container at a time. */}
          <ConsignmentStepper
            value={note.coefficients.length}
            onCommit={() => {}}
            readOnly
            aria-label={countLabel}
            onIncrement={note.addContainer}
            onDecrement={note.removeContainer}
            canDecrement={note.coefficients.length > 1}
            disabled={disabled}
          />
        </div>
        <div
          title={t(Namespace.PhotoReport, "factVolume")}
          className="flex h-8 items-center gap-2 rounded-lg border border-border-primary bg-white p-2 text-sm"
        >
          {/* Read-only: derived from the containers' count and coefficients. */}
          <span className="font-medium text-text-information">{note.factVolume ?? "—"}</span>
          {planned !== undefined && (
            <>
              <span className="text-text-body">{t(Namespace.PhotoReport, "ofVolume")}</span>
              <span className="text-text-body">{planned}</span>
            </>
          )}
          <span className="text-[#667489]">
            {t(Namespace.Settings, "locationSettings.cubicMeters")}
          </span>
        </div>
      </div>
    </div>
  );
};
